import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LifeBuoy, Send, ShieldCheck } from "lucide-react";

const SID = "main";

export default function Support() {
    const [msgs, setMsgs] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const end = useRef(null);

    useEffect(() => { api.get(`/support/history/${SID}`).then((r) => setMsgs(r.data || [])).catch(() => {}); }, []);
    useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

    const send = async () => {
        const t = text.trim();
        if (!t || loading) return;
        setText("");
        setMsgs((m) => [...m, { role: "user", text: t }]);
        setLoading(true);
        try {
            const r = await api.post("/support/chat", { session_id: SID, message: t });
            setMsgs((m) => [...m, { role: "assistant", text: r.data.reply, escalated: r.data.escalated }]);
        } catch (e) {
            setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. Please try again in a moment." }]);
        } finally { setLoading(false); }
    };

    return (
        <div className="flex h-[calc(100vh-9rem)] flex-col" data-testid="support-page">
            <div className="mb-3">
                <h1 className="flex items-center gap-2 font-display text-2xl font-700 text-primary"><LifeBuoy className="h-6 w-6 text-accent" /> Support</h1>
                <p className="text-sm text-muted-foreground">Ask anything about the club, 24/7. Account, billing, or safety matters are routed to our team.</p>
                <Link to="/resources" data-testid="support-resources-link" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-600 text-foreground hover:bg-secondary/80"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Resources &amp; Safety</Link>
            </div>
            <div className="flex-1 space-y-3 overflow-auto rounded-2xl bg-card p-4 shadow-sm">
                {msgs.length === 0 && <p className="text-sm text-muted-foreground">Hi! I'm the Single Social Club assistant. How can I help you today?</p>}
                {msgs.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div data-testid={`support-msg-${m.role}`} className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                            {m.text}
                            {m.escalated && <span className="mt-1 block text-xs font-600 text-accent">Escalated to the SSC team — we'll follow up with you.</span>}
                        </div>
                    </div>
                ))}
                {loading && <p className="text-xs text-muted-foreground">Assistant is typing…</p>}
                <div ref={end} />
            </div>
            <div className="mt-3 flex gap-2">
                <Input data-testid="support-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Type your question…" />
                <Button data-testid="support-send" onClick={send} disabled={loading} className="rounded-full"><Send className="h-4 w-4" /></Button>
            </div>
        </div>
    );
}
